// HTML sanitizer for rendered article bodies (fed to dangerouslySetInnerHTML).
//
// The body comes from RankVine's own LLM pipeline, but LLM output is not a trust
// boundary we should rely on — a prompt-injected or malformed body must never be
// able to execute script. The previous hand-rolled regex pass was bypassable
// (e.g. `<svg/onload=...>` with no whitespace, entity-encoded `javascript:`
// protocols, `<form action="javascript:...">`, style-based overlays), so we use
// DOMPurify — a mature, audited HTML sanitizer that parses the DOM rather than
// pattern-matching text.
//
// Config: allow only a conservative set of formatting tags/attributes suitable
// for an article body. script/style/svg/math and any event handlers or
// javascript:/data: URLs are stripped. DOMPurify blocks these by construction;
// the explicit allow-lists keep the surface tight (belt and braces).

import DOMPurify from "dompurify";

// Inline + block formatting tags an article body legitimately needs.
const ALLOWED_TAGS = [
  "p", "br", "hr", "span", "div",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "strong", "b", "em", "i", "u", "s", "sub", "sup", "small", "mark",
  "ul", "ol", "li",
  "blockquote", "pre", "code",
  "a", "img",
  "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption",
  "figure", "figcaption",
];

// Attributes safe to keep. Notably NO `style` (blocks overlay/clickjacking CSS)
// and NO `on*` handlers.
const ALLOWED_ATTR = ["href", "title", "alt", "src", "colspan", "rowspan", "lang", "dir"];

export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Never allow these tags even if listed elsewhere.
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta", "svg", "math", "form"],
    // Belt-and-braces: strip any event handler + form-action attributes.
    FORBID_ATTR: ["style", "form", "formaction", "action"],
    // Disallow data: URIs and unknown protocols; DOMPurify defaults already block
    // javascript:/vbscript: — this keeps only http(s)/mailto/tel/relative.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    // Keep the sanitized string; do not return a live DOM node.
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  });
}
